import { clsx } from 'clsx';
import { ArrowLeft } from '@music-vine/cadence/icons';
import { motion } from 'motion/react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { cloneElement, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DynamicLink } from '@/components/DynamicLink';
import { Button } from '@music-vine/cadence/ui';
import { Separator } from '@music-vine/cadence/ui';
import { Echo, Foxtrot } from '@music-vine/cadence/ui';
import { useIsClient } from '@/hooks/ui/useIsClient';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import { useTracking } from '@/providers/TrackingProvider';
import { useReturningUser } from '@/hooks/analytics/useReturningUser';
import type { Category as ComprehensiveListingCategory } from '@/api/comprehensivelistings';
import type { HTMLAttributes, ReactElement } from 'react';
import { getPopularIcon } from './get-popular-icon';
interface SeparatorCell {
  id: string;
  type: 'separator';
}
interface CategoryCell {
  category: {
    name: string;
    emoji?: string;
    slug?: string;
  };
  id: string;
  type: 'category';
}
interface TagCell {
  id: string;
  tag: {
    name: string;
    slug: string;
  };
  type: 'tag';
}
type GridCell = SeparatorCell | CategoryCell | TagCell;
type GridColumn = GridCell[];
interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
  heading: React.ReactNode;
  onlyNewUsers?: boolean;
}
interface ComponentWithHeadingChange {
  onHeadingChange?: (heading: React.ReactNode) => void;
}
const Layout = forwardRef<HTMLDivElement, LayoutProps>(({
  children,
  heading,
  onlyNewUsers,
  ...props
}, ref) => {
  const isClient = useIsClient();
  const {
    data: {
      isReturningUser
    }
  } = useReturningUser();
  const {
    isMd
  } = useTWScreens();
  const [mobileHeading, setMobileHeading] = useState<React.ReactNode>(heading);
  const hideCategories = !!onlyNewUsers && !!isReturningUser;
  return <div className={clsx('@container/comprehensive-listings flex w-full flex-col gap-4', !isClient && '[.initial-logged-in_&]:hidden', hideCategories && isClient && 'hidden')} ref={ref} {...props}>
        <Echo asChild className="flex flex-row gap-2">
          <h2>{isMd ? heading : mobileHeading}</h2>
        </Echo>
        {isMd ? children : cloneElement(children as ReactElement<ComponentWithHeadingChange>, {
      onHeadingChange: setMobileHeading
    })}
      </div>;
});
Layout.displayName = 'Layout';
interface ComprehensiveListingsProps {
  initialData?: ComprehensiveListingCategory[];
  onlyNewUsers?: boolean;
  showAllButton?: boolean;
  variant: 'sfx' | 'music' | 'luts' | 'stock-video';
}
type ListingAssetType = 'music' | 'motion' | 'sfx' | 'stock_footage';
const BrowseAllButton = ({
  assetType,
  href,
  label
}: {
  assetType: ListingAssetType;
  href: string;
  label: string;
}) => {
  const {
    initializeTracking
  } = useTracking();
  return <Button asChild className="w-min min-w-44 font-semibold" fontSize={{
    default: 'sm',
    '2xl': 'base'
  }} size="lg" variant="brand" data-sentry-element="Button" data-sentry-component="BrowseAllButton" data-sentry-source-file="index.tsx">
      <DynamicLink href={href} onClick={() => {
      void initializeTracking({
        assetType,
        endSlug: href,
        content: label,
        contentType: 'Browse All Button',
        access: 'Category Tags'
      });
    }} data-sentry-element="DynamicLink" data-sentry-source-file="index.tsx">
        {label}
      </DynamicLink>
    </Button>;
};

// Spreads the rest so it works as a `Button asChild` child
const TagLink = ({
  assetType,
  tag,
  ...props
}: {
  assetType: ListingAssetType;
  className?: string;
  tag: {
    name: string;
    slug: string;
  };
}) => {
  const {
    initializeTracking
  } = useTracking();
  return <DynamicLink {...props} href={tag.slug} onClick={() => {
    void initializeTracking({
      assetType,
      endSlug: tag.slug,
      content: tag.name,
      contentType: 'Tag',
      access: 'Category Tags'
    });
  }} data-sentry-element="DynamicLink" data-sentry-component="TagLink" data-sentry-source-file="index.tsx">
      {tag.name}
    </DynamicLink>;
};
const MusicListings = ({
  data,
  onHeadingChange,
  assetType = 'music'
}: {
  assetType?: ListingAssetType;
  data: ComprehensiveListingCategory[];
  onHeadingChange?: (heading: React.ReactNode) => void;
}) => {
  const pathname = usePathname();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const router = useRouter();
  const {
    isMd,
    isLg,
    isXl,
    is2Xl
  } = useTWScreens();

  // Reset selected category on route change
  useEffect(() => {
    setSelectedCategory(undefined);
  }, [router]);

  // Update heading when category changes (mobile only)
  useEffect(() => {
    if (!isMd && onHeadingChange) {
      const heading = <div className="flex items-center gap-2">
          {selectedCategory !== undefined && <Button aria-label="Back to categories" asChild borderRadius="full" icon onClick={() => setSelectedCategory(undefined)} size="xs" variant="light">
              <motion.button animate={{
            scale: 1,
            opacity: 1
          }} initial={{
            scale: 0.6,
            opacity: 0
          }} transition={{
            duration: 0.15
          }}>
                <ArrowLeft className="size-4 text-gray-950 dark:text-white" />
              </motion.button>
            </Button>}
          {selectedCategory ?? 'All categories'}
        </div>;
      onHeadingChange(heading);
    }
  }, [selectedCategory, isMd, onHeadingChange]);
  const totalItems = useMemo(() => data.reduce((acc, category) => acc + category.tag.length + 2,
  // +2 for the separator and heading
  0), [data]);
  const gridCols = useMemo(() => {
    if (is2Xl) {
      return 6;
    }
    if (isXl) {
      return 5;
    }
    if (isLg) {
      return 4;
    }
    if (isMd) {
      return 3;
    }
    return 2;
  }, [is2Xl, isXl, isLg, isMd]);
  const calculateRows = useCallback(() => Math.round(totalItems / gridCols), [gridCols, totalItems]);
  const columnData = useMemo((): GridColumn[] => {
    const rows = calculateRows();
    const result: GridColumn[] = [];
    let currentColumnData: GridColumn = [];
    let processedCells = 0;
    data.forEach((category, index) => {
      const isFirstRow = () => processedCells === 0;
      const isLastRow = () => processedCells === rows - 1;

      // only add separator if the current cell is not the first or last in the column
      if (!(isFirstRow() || isLastRow())) {
        currentColumnData.push({
          type: 'separator',
          id: `sep-${category.name}-${index}`
        });
        processedCells += 1;
      }

      // if the category starts on the last row, start a new column
      // so that the heading stays with the category contents
      if (isLastRow()) {
        result.push(currentColumnData);
        currentColumnData = [];
        processedCells = 0;
      }

      // add category heading
      currentColumnData.push({
        type: 'category',
        id: `cat-${category.name}-${index}`,
        category: {
          name: category.name,
          emoji: category.emoji,
          slug: category.slug
        }
      });
      processedCells += 1;

      // add up to the max number of tags per row (equal to rows per column)
      for (const categoryTag of category.tag) {
        const current: TagCell = {
          type: 'tag',
          id: `${category.name}-${categoryTag.name}`,
          tag: {
            name: categoryTag.name,
            slug: categoryTag.slug
          }
        };
        if (processedCells < rows) {
          currentColumnData.push(current);
          processedCells += 1;
        }
        if (processedCells === rows) {
          result.push(currentColumnData);
          currentColumnData = [];
          processedCells = 0;
        }
      }

      // On the last iteration, handle any remaining tags
      if (index === data.length - 1) {
        while (currentColumnData.length > 0) {
          let processedCellsInLoop = 0;
          const currentColumnDataInLoop: GridCell[] = [];
          for (const node of currentColumnData) {
            if (processedCellsInLoop < rows) {
              currentColumnDataInLoop.push(node);
              processedCellsInLoop += 1;
            }
          }
          if (currentColumnDataInLoop.length > 0) {
            result.push(currentColumnDataInLoop);
            currentColumnData.splice(0, processedCellsInLoop);
          }
        }
      }
    });
    return result;
  }, [calculateRows, data]);

  // Mobile view
  if (!isMd) {
    const selected = data.find(cat => cat.name === selectedCategory);
    // The group button opens the list on mobile, so the parent page joins it.
    // Last and prefixed, so it doesn't read as a duplicate of a same-named child.
    const selectedLinks = selected?.slug ? [...selected.tag, {
      name: `All ${selected.name.toLowerCase()}`,
      slug: selected.slug
    }] : selected?.tag;
    return <div className="flex flex-row flex-wrap gap-2">
        {selectedCategory === undefined ?
      // Show categories view
      data.map(category => <Button className="border-gray-150 dark:border-gray-800" fontSize={{
        default: 'sm',
        '2xl': 'base'
      }} key={category.id} onClick={() => setSelectedCategory(category.name)} size="lg" variant="light">
              {category.name}
            </Button>) :
      // Show tags for selected category
      <div className="w-full">
            <div className="flex flex-wrap gap-2">
              {selectedLinks?.map(tag => <Button asChild disabled={pathname === tag.slug} fontSize={{
            default: 'sm',
            '2xl': 'base'
          }} key={`${selectedCategory}-${tag.slug}`} size="lg" variant="light">
                  <TagLink assetType={assetType} tag={tag} />
                </Button>)}
            </div>
          </div>}
      </div>;
  }

  // Desktop view
  return <div className="-mx-2 flex gap-x-4 gap-y-2 pt-2" data-sentry-component="MusicListings" data-sentry-source-file="index.tsx">
      {columnData.map(column => <div className="flex flex-1 flex-col gap-x-4 gap-y-2" key={`col-${column[0].id}`}>
          {column.map(cell => {
        if (cell.type === 'separator') {
          return <div className="flex h-7 items-center" key={`separator-${cell.id}`}>
                  <Separator className="self-center" key={cell.id} />
                </div>;
        }
        if (cell.type === 'category') {
          return <Foxtrot asChild className="flex min-h-7 items-center px-2 font-sans! text-base! text-gray-950! dark:text-white!" key={cell.id}>
                  <h4>
                    {cell.category.slug ? <TagLink assetType={assetType} className="hover:underline" tag={{
                name: cell.category.name,
                slug: cell.category.slug
              }} /> : cell.category.name}
                  </h4>
                </Foxtrot>;
        }
        if (cell.type === 'tag') {
          return <Button asChild className="h-7 w-fit px-2 font-normal" disabled={pathname === cell.tag.slug} key={cell.id} size="sm" variant="transparent">
                  <TagLink assetType={assetType} tag={cell.tag} />
                </Button>;
        }
        return null;
      })}
        </div>)}
    </div>;
};
interface SFXListingsProps {
  data: ComprehensiveListingCategory[];
  onHeadingChange: (heading: React.ReactNode) => void;
  showAllButton?: boolean;
}
const SFXListings = ({
  data,
  onHeadingChange,
  showAllButton = false
}: SFXListingsProps) => {
  const [visibleTags, setVisibleTags] = useState<string | undefined>(undefined);
  const categoryRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const {
    initializeTracking
  } = useTracking();
  const handleCategory = (title: string) => {
    categoryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
    setVisibleTags(title);
  };
  const resetCategories = () => {
    setVisibleTags(undefined);
  };
  useEffect(resetCategories, [router]);

  // Update parent's heading whenever visibleTags changes
  useEffect(() => {
    const heading = <div className="flex items-center gap-2">
        {visibleTags !== undefined && <Button aria-label="Back to categories" asChild borderRadius="full" icon onClick={resetCategories} size="xs" variant="light">
            <motion.button animate={{
          scale: 1,
          opacity: 1
        }} initial={{
          scale: 0.6,
          opacity: 0
        }} transition={{
          duration: 0.15
        }}>
              <ArrowLeft className="size-4 text-gray-950 dark:text-white" />
            </motion.button>
          </Button>}
        {visibleTags ?? 'Popular categories'}
      </div>;
    onHeadingChange(heading);
  }, [visibleTags, onHeadingChange]);
  return <div className="flex flex-col gap-8" data-sentry-component="SFXListings" data-sentry-source-file="index.tsx">
      <div className="flex flex-row flex-wrap gap-2" ref={categoryRef}>
        {data?.map(category => {
        // Skip categories with no tags
        if (category.tag.length === 0) {
          return null;
        }
        const Icon = getPopularIcon({
          name: category.name
        });
        return <div className={cn(visibleTags === undefined || visibleTags === category.name ? 'visible' : 'hidden')} key={category.id}>
              <Button className={cn(visibleTags === undefined ? 'visible' : 'hidden', 'border-gray-150 font-sans dark:border-gray-800')} fontSize={{
            default: 'sm',
            '2xl': 'base'
          }} onClick={() => handleCategory(category.name)} size={{
            default: 'default',
            '2xl': 'lg'
          }} variant="light">
                {Icon ? <Icon className="size-4 text-pink-500" /> : <span className="text-xs">{category.emoji}</span>}

                {category.name}
              </Button>
              <div className={cn('flex flex-row flex-wrap gap-2', visibleTags === category.name ? 'visible' : 'hidden')}>
                {category.tag.map(tag => <Button asChild disabled={pathname === tag.slug} fontSize={{
              default: 'sm',
              '2xl': 'base'
            }} key={tag.slug} size={{
              default: 'default',
              '2xl': 'lg'
            }} variant="light">
                    <DynamicLink href={tag.slug} key={category.name + tag.name} onClick={() => {
                void initializeTracking({
                  assetType: 'sfx',
                  endSlug: tag.slug,
                  content: tag.name,
                  contentType: 'Tag',
                  access: 'Category Tags'
                });
              }}>
                      {tag.name}
                    </DynamicLink>
                  </Button>)}
              </div>
            </div>;
      })}
      </div>
      {showAllButton && <BrowseAllButton assetType="sfx" href="/sfx/all" label="Browse all sound effects" />}
    </div>;
};
const ComprehensiveListings = ({
  onlyNewUsers,
  initialData,
  variant,
  showAllButton = false
}: ComprehensiveListingsProps) => {
  const data = initialData;
  const defaultHeading = variant === 'sfx' ? 'Popular categories' : 'All categories';
  const [heading, setHeading] = useState<React.ReactNode>(defaultHeading);
  if (!data) {
    return null;
  }
  if (variant === 'stock-video') {
    return <div className="flex flex-col gap-8">
        <Layout heading={defaultHeading} onlyNewUsers={onlyNewUsers}>
          <MusicListings assetType="stock_footage" data={data} onHeadingChange={newHeading => setHeading(newHeading)} />
        </Layout>
        <BrowseAllButton assetType="stock_footage" href="/stock-video/explore" label="Browse all stock videos" />
      </div>;
  }
  if (variant === 'music' || variant === 'luts') {
    return <Layout heading={defaultHeading} onlyNewUsers={onlyNewUsers}>
        <MusicListings assetType={variant === 'luts' ? 'motion' : 'music'} data={data} onHeadingChange={newHeading => setHeading(newHeading)} />
      </Layout>;
  }
  return <Layout heading={heading} onlyNewUsers={onlyNewUsers} data-sentry-element="Layout" data-sentry-component="ComprehensiveListings" data-sentry-source-file="index.tsx">
      <SFXListings data={data} onHeadingChange={setHeading} showAllButton={showAllButton} data-sentry-element="SFXListings" data-sentry-source-file="index.tsx" />
    </Layout>;
};
export default ComprehensiveListings;